import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  NavigateFunction,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemText,
  Radio,
  Typography,
} from "@mui/material";
import { routeName } from "../../../../routes/routeName";
import { advisorEstimateScoreApi } from "../../../../api/advisor-estimate-score/advisor-estimate.api";
import NavigateListCustom from "../../../../components/navigation/NavigationListCustom";
import { colors } from "../../../../constants/colors";
import InputTextField from "../../../../components/input/inputTextField";
import { ButtonOutlined } from "../../../../components/button/ButtonCustoms";
import { useTranslation } from "react-i18next";

type Props = {};

const EvaluationScoresFromStudentsEstimateDetail: React.FC<Props> = (
  props: Props
) => {
  const { t } = useTranslation();
  const navigate: NavigateFunction = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const prop_year = queryParams.get("year");
  const prop_term = queryParams.get("term");
  const prop_course = queryParams.get("course");
  const prop_id_course_of_study = queryParams.get("id_course_of_study");

  const [dataDetail, setDataDetail] = useState<any>({});

  const getData = async (id: number) => {
    let query: any = {};

    const res =
      await advisorEstimateScoreApi.getScoreWithIdCourseAnnualEmployee(
        +id,
        query
      );

    const data = res.data;
    if (data.status < 300) {
      setDataDetail(res.data?.data ?? []);
    } else {
      setDataDetail([]);
    }
  };

  useEffect(() => {
    if (id) {
      getData(+id);
    }
  }, [id]);

  const optionEstimate = [
    {
      name: t("น้อยที่สุด (1)"),
      points: "1",
    },
    {
      name: t("น้อย (2)"),
      points: "2",
    },
    {
      name: t("ปานกลาง (3)"),
      points: "3",
    },
    {
      name: t("มาก (4)"),
      points: "4",
    },
    {
      name: t("มากที่สุด (5)"),
      points: "5",
    },
  ];

  // Grouping the data by `advisor_form_detail_name`
  const groupedData = dataDetail?.advisor_estimate_detail?.reduce(
    (acc: any, item: any) => {
      if (!acc[item.advisor_form_detail_name]) {
        acc[item.advisor_form_detail_name] = [];
      }
      acc[item.advisor_form_detail_name].push(item);
      return acc;
    },
    {}
  );

  return (
    <div>
      <NavigateListCustom
        navigateData={[
          {
            name: t("คะแนนประเมินจากนักศึกษา"),
            key: "1",
            pathName: routeName.advisor.EvaluationScoresFromStudents,
            onClick: () =>
              navigate(`${routeName.advisor.EvaluationScoresFromStudents}`),
          },
          {
            name: t("คะแนนประเมินจากนักศึกษา ปีการศึกษา {{year}} ภาคเรียนที่ {{term}}", {
              year: prop_year,
              term: prop_term,
            }),
            key: "2",
            pathName: routeName.advisor.EvaluationScoresFromStudents,
            onClick: () =>
              navigate(
                `${routeName.advisor.EvaluationScoresFromStudents}/${prop_id_course_of_study}/course?year=${prop_year}&term=${prop_term}`
              ),
          },
          {
            name: `${prop_course}`,
            key: "5",
            pathName: "",
          },
        ]}
      />

      {/* header */}
      <section className="my-4">
        <Typography variant="h3">{`${prop_course}`}</Typography>
      </section>

      {/*  */}
      <section className="my-4 mt-4">
        <Box textAlign="center">
          <Typography variant="h6">{t("คะแนนเฉลี่ย")}</Typography>

          <Typography
            variant="h1"
            fontWeight="bold"
            sx={{
              color: colors.orange,
            }}
          >
            {parseFloat(dataDetail?.advisor_estimate?.points).toFixed(1)}
          </Typography>
        </Box>
      </section>

      {/* header */}
      <section className="my-6">
        <Typography variant="h4">{`${
          dataDetail?.advisor_estimate?.advisor_form_name ?? "-"
        }`}</Typography>
      </section>

      <section className="my-6">
        {groupedData &&
          Object.entries(groupedData).map(([category, subcategories]: any) => (
            <Box key={category} mb={3}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                {category}
              </Typography>

              {/* Subcategories */}
              {subcategories.map((item: any) => (
                <Box
                  key={item.id_advisor_estimate_detail}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      ml: 4, // This ensures some margin for the subcategory name
                      flex: 1,
                    }}
                  >
                    {item.advisor_form_detail_sub_name}
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      ml: 4, // Same margin for estimate points
                      flex: 1,
                      textAlign: "right", // Align estimate points to the right
                    }}
                  >
                    {parseFloat(item.estimate_point).toFixed(1)}
                  </Typography>
                </Box>
              ))}
            </Box>
          ))}
      </section>

      <article className="col-span-12 sm:col-span-12 md:col-span-12 lg:col-span-12 space-y-2">
        <InputTextField
          value={dataDetail.advisor_estimate?.remark ?? ""}
          heading={t("รายละเอียด")}
          disabled
          row={3}
          maxRows={5}
          multiline
          inputHeight={"auto"}
        />

        <br />
        <br />
        <hr />
        <br />

        <Typography variant="h6" className="mb-4">
          {t("ความคิดเห็นถึงหมด")}
        </Typography>
        {dataDetail.remark &&
          dataDetail.remark.map((item: any, index: any) => (
            <Card
              key={item}
              variant="outlined"
              sx={{
                display: "flex",
                alignItems: "center",
                p: 2,
                borderRadius: "12px", // Smooth border radius
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexGrow: 1,
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "250px",
                      display: "block",
                    }}
                  >
                    {item}
                  </Typography>
                </Box>
              </Box>
            </Card>
          ))}
      </article>

      <div
        style={{
          display: "flex",
          gap: "10px",
          justifyContent: "center",
          marginTop: "20px",
        }}
      >
        <ButtonOutlined
          onClick={() => navigate(-1)}
          mode="del"
          buttonName={t("ย้อนกลับ")}
        />
      </div>
    </div>
  );
};

export default EvaluationScoresFromStudentsEstimateDetail;
